import { useEffect, useRef } from 'react';
import { dateStringFromDate } from '../../../common/src/date_string.ts';
import { Hymn } from '../../../common/src/model.ts';
import { singingPlanCompletionDurationMs } from '../../../common/src/org.ts';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { songViews } from '../data/song_views.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { useSingingPlanCompletions } from '../data/use_singing_plan_completions.ts';
import { Stopwatch } from '../util/stopwatch.ts';
import { useIsAppActive } from '../util/use_is_app_active.tsx';

export const completionPollIntervalMs = 1000;

// minimum accrued time for a song view to be recorded
// (also avoids double counting in development, where useEffect runs twice)
const minimumSongViewDurationMs = 1000;

// Records song views and singing plan completions from time accrued on the player stopwatch.
// The stopwatch counts time while audio is playing or while the maximized player displays the song.
//
// A completion is recorded the moment the viewing threshold is crossed - NOT when the song is
// closed - because the mini player keeps the current song open indefinitely (issue #3155).
// Recording at the threshold also survives the app being killed and attributes the completion
// to the date the song was actually viewed, rather than when the next song was opened.
// Songs closed before the threshold is crossed are recorded as raw song views, which do not
// count toward the singing plan streak.
//
// This hook is the sole owner of song view recording; marking a singing plan song completed
// (useSingingPlanCompletions) only updates streak state and never records a song view.
export function useSongViewRecorder({ hymn, stopwatch }: { hymn: Hymn | undefined; stopwatch: Stopwatch }) {
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const isAppActive = useIsAppActive();
  const { currentOrg } = useCurrentOrg();
  const { markSongCompleted } = useSingingPlanCompletions();

  // Refs keep the recording effect below from tearing down (which clears the stopwatch)
  // whenever the household or org changes identity mid-song, e.g. due to a server sync.
  const markSongCompletedRef = useRef(markSongCompleted);
  const currentOrgIdRef = useRef(currentOrg?.id);
  useEffect(() => {
    markSongCompletedRef.current = markSongCompleted;
    currentOrgIdRef.current = currentOrg?.id;
  }, [markSongCompleted, currentOrg?.id]);

  // Count time viewing the song toward the song view
  useEffect(() => {
    if (!isPlayerMaximized || !hymn || !isAppActive) {
      return;
    }
    const pdfStopwatchSubscription = stopwatch.subscribe();
    return () => {
      stopwatch.unsubscribe(pdfStopwatchSubscription);
    };
  }, [isPlayerMaximized, hymn, stopwatch, isAppActive]);

  // Record the completion the moment the viewing threshold is crossed;
  // record songs closed before the threshold as raw song views.
  useEffect(() => {
    const songSlug = hymn?.slug;
    if (!songSlug) {
      return;
    }
    let completionRecorded = false;
    const intervalId = window.setInterval(() => {
      if (stopwatch.totalTimeMs < singingPlanCompletionDurationMs) {
        return;
      }
      completionRecorded = true;
      window.clearInterval(intervalId);
      const dateString = dateStringFromDate(new Date());
      markSongCompletedRef.current(dateString, songSlug);
      songViews.add({ songSlug, durationMs: stopwatch.totalTimeMs, orgId: currentOrgIdRef.current });
      void songViews.uploadToServer();
    }, completionPollIntervalMs);
    return () => {
      window.clearInterval(intervalId);
      if (stopwatch.totalTimeMs < minimumSongViewDurationMs) {
        return;
      }
      const durationMs = stopwatch.totalTimeMs;
      stopwatch.clear();
      if (!completionRecorded) {
        songViews.add({ songSlug, durationMs, orgId: currentOrgIdRef.current });
        void songViews.uploadToServer();
      }
    };
  }, [hymn, stopwatch]);
}
